/**
 * Gradient fills that degrade instead of exploding.
 *
 * `FillGradient` rasterises its colour ramp through a 2D canvas. That is fine
 * in a browser and fatal under vitest in node, where `document` exists but
 * `getContext('2d')` returns null — building one throws
 * `Cannot read properties of null (reading 'createLinearGradient')`.
 *
 * That matters because the modules that want gradients are also modules with
 * genuinely valuable node tests. `Stage` is the clearest case: its letterbox
 * spill is decoration, but its parallax and design-space round-trip are the
 * load-bearing behaviour the whole hit-testing story rests on, and adding a
 * gradient to the first should not cost the tests for the second.
 *
 * So: ask for a gradient, get a gradient where one can be built and a flat
 * approximation everywhere else. Callers pass the colour the flat fallback
 * should use, because only they know which stop is representative.
 */

import { FillGradient } from 'pixi.js';

import type { FillStyle } from 'pixi.js';

/** One stop, in the form the Pixi gradient wants. */
export interface Stop {
  offset: number;
  /** 0xRRGGBB. */
  color: number;
  /** 0..1. */
  alpha: number;
}

/** Cached, because probing for a 2D context allocates a canvas. */
let supported: boolean | null = null;

/**
 * Whether a `FillGradient` can be constructed in this environment.
 *
 * Probed once and cached. False under vitest in node, true in any browser.
 */
export function gradientsAvailable(): boolean {
  if (supported !== null) return supported;
  try {
    const canvas = globalThis.document?.createElement?.('canvas');
    supported = Boolean(canvas?.getContext?.('2d'));
  } catch {
    supported = false;
  }
  return supported;
}

/** Reset the cached probe. Tests only. */
export function resetGradientSupport(): void {
  supported = null;
}

function rgba({ color, alpha }: Stop): string {
  return `rgba(${(color >> 16) & 0xff}, ${(color >> 8) & 0xff}, ${color & 0xff}, ${alpha})`;
}

/** The stop nearest the middle, used as the flat fallback. */
function representative(stops: Stop[]): Stop {
  let best = stops[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const stop of stops) {
    const distance = Math.abs(stop.offset - 0.5);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = stop;
    }
  }
  return best;
}

/**
 * A linear gradient between two points in the shape's local 0..1 space.
 *
 * @param start - gradient start, in local space.
 * @param end - gradient end, in local space.
 * @param stops - at least one stop.
 */
export function linearFill(
  start: { x: number; y: number },
  end: { x: number; y: number },
  stops: Stop[],
): FillStyle {
  if (!gradientsAvailable()) {
    const flat = representative(stops);
    return { color: flat.color, alpha: flat.alpha };
  }
  return {
    fill: new FillGradient({
      type: 'linear',
      start,
      end,
      colorStops: stops.map((stop) => ({ offset: stop.offset, color: rgba(stop) })),
    }),
  };
}

/**
 * A radial gradient from the centre of the shape's local space outward.
 *
 * @param stops - at least one stop, `offset` 0 at the centre.
 */
export function radialFill(stops: Stop[]): FillStyle {
  if (!gradientsAvailable()) {
    const flat = representative(stops);
    return { color: flat.color, alpha: flat.alpha };
  }
  return {
    fill: new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.5 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.5,
      colorStops: stops.map((stop) => ({ offset: stop.offset, color: rgba(stop) })),
    }),
  };
}

/**
 * A gradient suitable for a `TextStyle.fill`.
 *
 * Text cannot take a flat `FillStyle` object the way a Graphics fill can, so
 * this returns either the gradient or a plain colour number.
 */
export function textFill(stops: Stop[]): FillGradient | number {
  if (!gradientsAvailable()) return representative(stops).color;
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: stops.map((stop) => ({ offset: stop.offset, color: rgba(stop) })),
  });
}
